"use client";

import React, {
    type CSSProperties,
    type FormEvent,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import clsx from "clsx";
import {
    AlertTriangle,
    Check,
    CheckCircle2,
    ChevronDown,
    Clock3,
    LoaderCircle,
    Lock,
    Mail,
    MapPin,
    MessageSquare,
    Phone,
    Send,
    ShieldCheck,
    Star,
    User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type GoogleContactFormStatus = "idle" | "loading" | "success" | "error";
export type GoogleContactFormBuiltInFieldName =
    | "service"
    | "suburb"
    | "firstName"
    | "lastName"
    | "phone"
    | "email"
    | "message";

export type GoogleContactFormFieldName =
    | GoogleContactFormBuiltInFieldName
    | (string & {});

export type GoogleContactFormValues = Record<string, string>;

export type GoogleContactFormErrors = Partial<
    Record<GoogleContactFormFieldName | "captcha", string>
>;

export type GoogleContactFormFieldOption = {
    label: string;
    value: string;
};

export type GoogleContactFormServiceOption = GoogleContactFormFieldOption;
export type GoogleContactFormFieldType =
    | "text"
    | "email"
    | "tel"
    | "textarea"
    | "select"
    | "dropdown"
    | "checkbox"
    | "hidden";
export type GoogleContactFormFieldIcon =
    | "user"
    | "mail"
    | "phone"
    | "map-pin"
    | "message"
    | "shield"
    | "star"
    | "clock"
    | "check";
export type GoogleContactFormFieldWidth = "full" | "half";

type GoogleContactFormBaseField = {
    name: string;
    label: string;
    type: Exclude<GoogleContactFormFieldType, "hidden">;
    required?: boolean;
    requiredMessage?: string;
    placeholder?: string;
    defaultValue?: string | number | boolean | null;
    minLength?: number;
    maxLength?: number;
    autoComplete?: string;
    icon?: GoogleContactFormFieldIcon;
    width?: GoogleContactFormFieldWidth;
    disabled?: boolean;
};

export type GoogleContactFormInputField = GoogleContactFormBaseField & {
    type: "text" | "email" | "tel";
};

export type GoogleContactFormTextareaField = GoogleContactFormBaseField & {
    type: "textarea";
    rows?: number;
};

export type GoogleContactFormSelectField = GoogleContactFormBaseField & {
    type: "select" | "dropdown";
    options: GoogleContactFormFieldOption[];
};

export type GoogleContactFormCheckboxField = Omit<
    GoogleContactFormBaseField,
    "placeholder" | "autoComplete" | "minLength" | "maxLength"
> & {
    type: "checkbox";
    checkboxLabel?: ReactNode;
    description?: ReactNode;
    defaultChecked?: boolean;
    checkedValue?: string;
    uncheckedValue?: string;
};

export type GoogleContactFormHiddenField = {
    name: string;
    type: "hidden";
    value?: string | number | boolean | null;
    defaultValue?: string | number | boolean | null;
};

export type GoogleContactFormSchemaField =
    | GoogleContactFormInputField
    | GoogleContactFormTextareaField
    | GoogleContactFormSelectField
    | GoogleContactFormCheckboxField
    | GoogleContactFormHiddenField;

export type GoogleContactFormSchema = {
    fields: GoogleContactFormSchemaField[];
};

export type GoogleContactFormTrustIcon = "star" | "shield" | "clock";

export type GoogleContactFormTrustItem = {
    label: string;
    icon?: GoogleContactFormTrustIcon;
};

export type GoogleContactFormImage = {
    src: string | { src: string };
    alt: string;
    position?: "left" | "right" | "top";
    aspectRatio?: string;
    objectFit?: CSSProperties["objectFit"];
    objectPosition?: CSSProperties["objectPosition"];
    background?: string;
    caption?: ReactNode;
};

export type GoogleContactFormPayload = {
    form: string;
    captchaToken: string;
    data: Record<string, string>;
};

export type GoogleContactFormBuildPayloadContext = {
    values: GoogleContactFormValues;
    formName: string;
    captchaToken: string;
    hiddenPayload: Record<string, string>;
    schema: GoogleContactFormSchema;
};

export type GoogleContactFormProps = {
    apiUrl?: string;
    workerUrl?: string;
    formName?: string;
    captchaElement?: ReactNode;
    getCaptchaToken?: () => string | null | Promise<string | null>;
    resetCaptcha?: () => void;
    buildPayload?: (
        context: GoogleContactFormBuildPayloadContext,
    ) => GoogleContactFormPayload;
    schema?: GoogleContactFormSchema;
    extraFields?: GoogleContactFormSchemaField[];
    serviceOptions?: GoogleContactFormServiceOption[];
    initialValues?: Record<string, string | number | boolean | null | undefined>;
    requiredFields?: GoogleContactFormFieldName[];
    hiddenPayload?: Record<string, string | number | boolean | null | undefined>;
    title?: ReactNode;
    subtitle?: ReactNode;
    servicePlaceholder?: string;
    submitText?: string;
    loadingText?: string;
    successTitle?: ReactNode;
    successMessage?: ReactNode;
    errorTitle?: ReactNode;
    errorMessage?: ReactNode;
    captchaRequiredMessage?: string;
    footerText?: ReactNode;
    loadingFooterText?: ReactNode;
    errorFooterText?: ReactNode;
    trustItems?: GoogleContactFormTrustItem[];
    image?: GoogleContactFormImage;
    accentColor?: string;
    className?: string;
    style?: CSSProperties;
    statusOverride?: GoogleContactFormStatus;
    errorsOverride?: GoogleContactFormErrors;
    onSuccess?: (response: unknown, payload: GoogleContactFormPayload) => void;
    onError?: (error: unknown, payload?: GoogleContactFormPayload) => void;
};

declare global {
    interface Window {
        grecaptcha?: {
            getResponse?: () => string;
            reset?: () => void;
        };
    }
}

type CssVarStyle = CSSProperties & Record<`--${string}`, string | number | undefined>;

const defaultServiceOptions: GoogleContactFormServiceOption[] = [
    { label: "General Pest Control", value: "General Pest Control" },
    { label: "Termite Inspection", value: "Termite Inspection" },
    { label: "Termite Treatment", value: "Termite Treatment" },
    { label: "Cockroach Control", value: "Cockroach Control" },
    { label: "Rodent Control", value: "Rodent Control" },
    { label: "Spider Control", value: "Spider Control" },
    { label: "Ant Control", value: "Ant Control" },
    { label: "End of Lease Pest Control", value: "End of Lease Pest Control" },
];

const defaultTrustItems: GoogleContactFormTrustItem[] = [
    { label: "5-Star Rated", icon: "star" },
    { label: "Licensed & Insured", icon: "shield" },
    { label: "Same-Day Service", icon: "clock" },
];

const defaultRequiredFields: GoogleContactFormFieldName[] = [
    "service",
    "suburb",
    "firstName",
    "lastName",
    "phone",
    "email",
    "message",
];
const emptyExtraFields: GoogleContactFormSchemaField[] = [];

const fieldLabels: Record<GoogleContactFormBuiltInFieldName, string> = {
    service: "Service Needed",
    suburb: "Suburb",
    firstName: "First Name",
    lastName: "Last Name",
    phone: "Phone Number",
    email: "Email Address",
    message: "Tell us about your pest problem...",
};

const trustIcons = {
    star: Star,
    shield: ShieldCheck,
    clock: Clock3,
};

const fieldIcons: Record<GoogleContactFormFieldIcon, LucideIcon> = {
    user: User,
    mail: Mail,
    phone: Phone,
    "map-pin": MapPin,
    message: MessageSquare,
    shield: ShieldCheck,
    star: Star,
    clock: Clock3,
    check: CheckCircle2,
};

function buildDefaultSchema(
    serviceOptions: GoogleContactFormServiceOption[],
    servicePlaceholder: string,
    requiredFields: GoogleContactFormFieldName[],
): GoogleContactFormSchema {
    const requiredSet = new Set(requiredFields);

    return {
        fields: [
            {
                name: "service",
                label: fieldLabels.service,
                type: "select",
                required: requiredSet.has("service"),
                placeholder: servicePlaceholder,
                options: serviceOptions,
                icon: "shield",
            },
            {
                name: "suburb",
                label: fieldLabels.suburb,
                type: "text",
                required: requiredSet.has("suburb"),
                placeholder: "Suburb",
                autoComplete: "address-level2",
                icon: "map-pin",
            },
            {
                name: "firstName",
                label: fieldLabels.firstName,
                type: "text",
                required: requiredSet.has("firstName"),
                placeholder: "First name",
                autoComplete: "given-name",
                icon: "user",
                width: "half",
            },
            {
                name: "lastName",
                label: fieldLabels.lastName,
                type: "text",
                required: requiredSet.has("lastName"),
                placeholder: "Last name",
                autoComplete: "family-name",
                icon: "user",
                width: "half",
            },
            {
                name: "phone",
                label: fieldLabels.phone,
                type: "tel",
                required: requiredSet.has("phone"),
                placeholder: "Phone number",
                autoComplete: "tel",
                icon: "phone",
            },
            {
                name: "email",
                label: fieldLabels.email,
                type: "email",
                required: requiredSet.has("email"),
                placeholder: "Email address",
                autoComplete: "email",
                icon: "mail",
            },
            {
                name: "message",
                label: fieldLabels.message,
                type: "textarea",
                required: requiredSet.has("message"),
                placeholder: "Tell us what you need help with...",
                icon: "message",
            },
        ],
    };
}

const googleContactFormCss = `
.google-contact-form {
    width: 100%;
    max-width: 560px;
    color: #18181b;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    container-type: inline-size;
}

.google-contact-form * {
    box-sizing: border-box;
}

.google-contact-form-card {
    width: 100%;
    border: 1px solid rgba(17, 24, 39, 0.11);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 18px 54px rgba(15, 23, 42, 0.14);
    padding: clamp(18px, 3.4vw, 28px);
}

.google-contact-form--with-image {
    position: relative;
    isolation: isolate;
    max-width: none;
    min-height: clamp(620px, 72vh, 780px);
    aspect-ratio: var(--google-contact-form-image-aspect-ratio, auto);
    overflow: hidden;
    background-color: var(--google-contact-form-image-background, #f8fafc);
    background-image: var(--google-contact-form-image-src);
    background-position: var(--google-contact-form-image-position, center);
    background-repeat: no-repeat;
    background-size: var(--google-contact-form-image-size, cover);
}

.google-contact-form-shell {
    width: 100%;
}

.google-contact-form--with-image .google-contact-form-shell {
    position: relative;
    z-index: 1;
    display: flex;
    min-height: inherit;
    align-items: center;
    justify-content: flex-end;
    padding: clamp(18px, 4vw, 48px);
}

.google-contact-form--with-image::before {
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        linear-gradient(
            90deg,
            rgba(15, 23, 42, 0.26),
            rgba(15, 23, 42, 0.08) 42%,
            rgba(255, 255, 255, 0.44) 100%
        );
    content: "";
    pointer-events: none;
}

.google-contact-form--image-right .google-contact-form-shell {
    justify-content: flex-start;
}

.google-contact-form--image-right::before {
    background:
        linear-gradient(
            270deg,
            rgba(15, 23, 42, 0.26),
            rgba(15, 23, 42, 0.08) 42%,
            rgba(255, 255, 255, 0.44) 100%
        );
}

.google-contact-form--image-top .google-contact-form-shell {
    align-items: flex-end;
    justify-content: center;
}

.google-contact-form--image-top::before {
    background:
        linear-gradient(
            180deg,
            rgba(15, 23, 42, 0.2),
            rgba(15, 23, 42, 0.06) 44%,
            rgba(255, 255, 255, 0.5) 100%
        );
}

.google-contact-form--with-image .google-contact-form-card {
    max-width: min(100%, 560px);
}

.google-contact-form-background-description {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
}

.google-contact-form-trust {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 22px;
}

.google-contact-form-trust-item {
    display: inline-flex;
    min-width: 0;
    min-height: 38px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px solid rgba(17, 24, 39, 0.12);
    border-radius: 8px;
    background: #ffffff;
    color: #1f2937;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.1;
    padding: 7px 8px;
    text-align: center;
}

.google-contact-form-trust-icon {
    display: inline-flex;
    flex: 0 0 auto;
    color: var(--google-contact-form-accent);
}

.google-contact-form-trust-item span:last-child {
    min-width: 0;
    overflow-wrap: anywhere;
}

.google-contact-form-header {
    margin-bottom: 20px;
    text-align: center;
}

.google-contact-form-title {
    margin: 0;
    color: #101014;
    font-size: clamp(24px, 2.7vw, 30px);
    font-weight: 900;
    letter-spacing: 0;
    line-height: 1.08;
}

.google-contact-form-subtitle {
    max-width: 360px;
    margin: 10px auto 0;
    color: #3f3f46;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.55;
}

.google-contact-form-state-banner {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 76px;
    margin-bottom: 14px;
    border-radius: 8px;
    padding: 15px 16px;
}

.google-contact-form-state-banner--success {
    border: 1px solid rgba(34, 197, 94, 0.35);
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(240, 253, 244, 0.96));
    color: #14532d;
}

.google-contact-form-state-banner--error {
    border: 1px solid rgba(239, 68, 68, 0.36);
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(254, 242, 242, 0.98));
    color: #7f1d1d;
}

.google-contact-form-state-icon {
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
}

.google-contact-form-state-banner--success .google-contact-form-state-icon {
    background: #41aa5d;
    color: #ffffff;
}

.google-contact-form-state-banner--error .google-contact-form-state-icon {
    background: #ffffff;
    color: #ef1f1f;
}

.google-contact-form-state-title {
    margin: 0;
    color: currentColor;
    font-size: 15px;
    font-weight: 900;
    line-height: 1.3;
}

.google-contact-form-state-message {
    margin: 3px 0 0;
    color: #27272a;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.45;
}

.google-contact-form-form {
    display: grid;
    gap: 10px;
}

.google-contact-form-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.google-contact-form-field {
    display: grid;
    gap: 5px;
    min-width: 0;
}

.google-contact-form-control {
    position: relative;
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) 24px;
    gap: 8px;
    align-items: center;
    min-height: 52px;
    border: 1px solid rgba(17, 24, 39, 0.15);
    border-radius: 8px;
    background: #ffffff;
    color: #18181b;
    padding: 7px 10px;
    transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;
}

.google-contact-form-control:focus-within {
    border-color: color-mix(in srgb, var(--google-contact-form-accent) 52%, #ffffff);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--google-contact-form-accent) 13%, transparent);
}

.google-contact-form-control--no-status {
    grid-template-columns: 22px minmax(0, 1fr);
}

.google-contact-form-field--textarea .google-contact-form-control {
    align-items: start;
    min-height: 94px;
    padding-top: 11px;
}

.google-contact-form-checkbox-control {
    align-items: start;
    min-height: 52px;
    cursor: pointer;
    padding-top: 11px;
    padding-bottom: 11px;
}

.google-contact-form-checkbox-control.google-contact-form-control--no-status {
    grid-template-columns: 18px minmax(0, 1fr);
}

.google-contact-form-checkbox-input {
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    accent-color: var(--google-contact-form-accent);
    cursor: pointer;
}

.google-contact-form-checkbox-text {
    color: #111827;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.35;
}

.google-contact-form-checkbox-description {
    margin-top: 3px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
}

.google-contact-form-field-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #8b8f99;
}

.google-contact-form-field-body {
    display: grid;
    min-width: 0;
    gap: 2px;
}

.google-contact-form-label {
    color: #7c8490;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
}

.google-contact-form-input,
.google-contact-form-select,
.google-contact-form-textarea {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #111827;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    padding: 0;
}

.google-contact-form-select {
    appearance: none;
    cursor: pointer;
    font-size: 14px;
    padding-right: 4px;
}

.google-contact-form-textarea {
    min-height: 56px;
    resize: vertical;
}

.google-contact-form-input::placeholder,
.google-contact-form-textarea::placeholder {
    color: #9ca3af;
    opacity: 1;
}

.google-contact-form-status-icon {
    display: inline-flex;
    justify-content: flex-end;
    color: #9ca3af;
}

.google-contact-form-status-icon--success {
    color: #40a863;
}

.google-contact-form-status-icon--error {
    color: #ee1d23;
}

.google-contact-form-field--error .google-contact-form-control {
    border-color: #ff4a4a;
    background: linear-gradient(135deg, rgba(255, 241, 242, 0.98), #ffffff);
}

.google-contact-form-error {
    margin: 0;
    color: #ee1d23;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
}

.google-contact-form-captcha {
    display: grid;
    min-height: 72px;
    align-items: center;
    justify-items: center;
    overflow: hidden;
    border: 1px solid rgba(17, 24, 39, 0.12);
    border-radius: 8px;
    background: #f9fafb;
    padding: 9px;
}

.google-contact-form-captcha:empty {
    display: none;
}

.google-contact-form-submit {
    display: inline-flex;
    width: 100%;
    min-height: 56px;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(
        135deg,
        var(--google-contact-form-accent),
        color-mix(in srgb, var(--google-contact-form-accent) 78%, #111827)
    );
    color: #ffffff;
    cursor: pointer;
    font-size: 18px;
    font-weight: 900;
    line-height: 1;
    padding: 15px 18px;
    box-shadow: 0 10px 24px color-mix(in srgb, var(--google-contact-form-accent) 24%, transparent);
    transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
}

.google-contact-form-submit:hover {
    transform: translateY(-1px);
    box-shadow: 0 13px 30px color-mix(in srgb, var(--google-contact-form-accent) 29%, transparent);
}

.google-contact-form-submit:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--google-contact-form-accent) 30%, transparent);
    outline-offset: 3px;
}

.google-contact-form-submit:disabled {
    cursor: wait;
    opacity: 0.78;
    transform: none;
}

.google-contact-form-spinner {
    animation: google-contact-form-spin 900ms linear infinite;
}

.google-contact-form-footer {
    display: flex;
    max-width: 360px;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
    margin: 12px auto 0;
    color: #52525b;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
}

.google-contact-form-footer svg {
    flex: 0 0 auto;
    margin-top: 2px;
}

@keyframes google-contact-form-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@media (max-width: 560px) {
    .google-contact-form-card {
        padding: 16px;
    }

    .google-contact-form--with-image {
        max-width: none;
        min-height: auto;
    }

    .google-contact-form--with-image .google-contact-form-shell {
        min-height: auto;
        justify-content: center;
        padding: 16px;
    }

    .google-contact-form--with-image::before,
    .google-contact-form--image-right::before,
    .google-contact-form--image-top::before {
        background: rgba(255, 255, 255, 0.58);
    }

    .google-contact-form-trust {
        gap: 6px;
        margin-bottom: 18px;
    }

    .google-contact-form-trust-item {
        min-height: 34px;
        gap: 4px;
        font-size: 10.5px;
        padding: 6px 4px;
    }

    .google-contact-form-title {
        font-size: 24px;
    }

    .google-contact-form-subtitle {
        font-size: 13px;
        line-height: 1.45;
    }

    .google-contact-form-submit {
        min-height: 52px;
        font-size: 17px;
    }
}

@container (max-width: 480px) {
    .google-contact-form-trust {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@container (max-width: 340px) {
    .google-contact-form-row {
        gap: 8px;
    }

    .google-contact-form-row .google-contact-form-control {
        grid-template-columns: 16px minmax(0, 1fr) 16px;
        gap: 4px;
        padding-inline: 7px;
    }

    .google-contact-form-row .google-contact-form-control--no-status {
        grid-template-columns: 16px minmax(0, 1fr);
    }

    .google-contact-form-row .google-contact-form-field-icon svg,
    .google-contact-form-row .google-contact-form-status-icon svg {
        width: 16px;
        height: 16px;
    }

    .google-contact-form-row .google-contact-form-label {
        font-size: 10px;
    }

    .google-contact-form-row .google-contact-form-input,
    .google-contact-form-row .google-contact-form-select {
        font-size: 14px;
        line-height: 1.22;
    }
}

@container (max-width: 260px) {
    .google-contact-form-trust {
        grid-template-columns: 1fr;
    }

    .google-contact-form-row {
        grid-template-columns: 1fr;
    }
}
`;

function getDefaultCaptchaToken() {
    if (typeof window === "undefined") return null;
    return window.grecaptcha?.getResponse?.() ?? null;
}

function resetDefaultCaptcha() {
    if (typeof window === "undefined") return;
    window.grecaptcha?.reset?.();
}

function getPageOrigin() {
    if (typeof window === "undefined") return "";
    return window.location.origin;
}

function getPageUrl() {
    if (typeof window === "undefined") return "";
    return window.location.href;
}

function getImageSrc(src: GoogleContactFormImage["src"]) {
    return typeof src === "string" ? src : src.src;
}

function getImageBackgroundSize(objectFit: GoogleContactFormImage["objectFit"]) {
    return objectFit === "contain" ? "contain" : "cover";
}

function toPayloadString(value: string | number | boolean | null | undefined) {
    if (value === null || value === undefined) return "";
    return String(value);
}

function getCheckboxCheckedValue(field: GoogleContactFormCheckboxField) {
    return field.checkedValue ?? "true";
}

function getCheckboxUncheckedValue(field: GoogleContactFormCheckboxField) {
    return field.uncheckedValue ?? "false";
}

function isCheckboxChecked(field: GoogleContactFormCheckboxField, value: string) {
    return value === getCheckboxCheckedValue(field) || value === "true";
}

function getInitialFieldValue(
    field: GoogleContactFormSchemaField,
    initialValues?: Record<string, string | number | boolean | null | undefined>,
) {
    const initialValue = initialValues?.[field.name];

    if (initialValue !== undefined) {
        return toPayloadString(initialValue);
    }

    if (field.type === "hidden") {
        return toPayloadString(field.value ?? field.defaultValue);
    }

    if (field.type === "checkbox") {
        if (field.defaultChecked) return getCheckboxCheckedValue(field);
        return toPayloadString(field.defaultValue) || getCheckboxUncheckedValue(field);
    }

    return toPayloadString(field.defaultValue);
}

function getInitialValues(
    schema: GoogleContactFormSchema,
    initialValues?: Record<string, string | number | boolean | null | undefined>,
) {
    return schema.fields.reduce<GoogleContactFormValues>((acc, field) => {
        acc[field.name] = getInitialFieldValue(field, initialValues);
        return acc;
    }, {});
}

function getSchemaStateKey(schema: GoogleContactFormSchema) {
    return JSON.stringify(
        schema.fields.map((field) => {
            if (field.type === "hidden") {
                return {
                    name: field.name,
                    type: field.type,
                    value: field.value,
                    defaultValue: field.defaultValue,
                };
            }

            return {
                name: field.name,
                type: field.type,
                defaultValue: field.defaultValue,
                defaultChecked:
                    field.type === "checkbox" ? field.defaultChecked : undefined,
                checkedValue:
                    field.type === "checkbox" ? field.checkedValue : undefined,
                uncheckedValue:
                    field.type === "checkbox" ? field.uncheckedValue : undefined,
            };
        }),
    );
}

function getFieldLabel(field: GoogleContactFormSchemaField) {
    return field.type === "hidden" ? field.name : field.label;
}

function getDefaultFieldIcon(field: GoogleContactFormSchemaField) {
    if (field.type === "hidden") return User;
    if (field.icon) return fieldIcons[field.icon];
    if (field.type === "email" || field.name.toLowerCase().includes("email")) return Mail;
    if (field.type === "tel" || field.name.toLowerCase().includes("phone")) return Phone;
    if (field.type === "textarea" || field.name.toLowerCase().includes("message")) return MessageSquare;
    if (field.type === "select" || field.type === "dropdown") return ShieldCheck;
    if (field.type === "checkbox") return CheckCircle2;
    if (field.name.toLowerCase().includes("suburb")) return MapPin;
    return User;
}

function normalizePhone(value: string) {
    return value.replace(/[^\d+]/g, "");
}

function isValidAustralianPhone(value: string) {
    const phone = normalizePhone(value);
    const localDigits = phone.startsWith("+61")
        ? `0${phone.slice(3)}`
        : phone.startsWith("61")
          ? `0${phone.slice(2)}`
          : phone;

    const digitsOnly = localDigits.replace(/\D/g, "");

    return (
        /^04\d{8}$/.test(digitsOnly) ||
        /^0[2378]\d{8}$/.test(digitsOnly) ||
        /^13\d{4}$/.test(digitsOnly) ||
        /^1[38]00\d{6}$/.test(digitsOnly)
    );
}

function validateValues(
    values: GoogleContactFormValues,
    fields: GoogleContactFormSchemaField[],
) {
    const errors: GoogleContactFormErrors = {};

    for (const field of fields) {
        if (field.type === "hidden") continue;

        const value = values[field.name]?.trim() ?? "";

        if (field.required) {
            const isMissing =
                field.type === "checkbox"
                    ? !isCheckboxChecked(field, value)
                    : !value;

            if (isMissing) {
                errors[field.name] =
                    field.requiredMessage ??
                    (field.type === "email"
                        ? "Email address is required."
                        : `${getFieldLabel(field)} is required.`);
                continue;
            }
        }

        if (field.type === "checkbox") continue;

        if (field.minLength && value && value.length < field.minLength) {
            errors[field.name] = `Must be at least ${field.minLength} characters.`;
            continue;
        }

        if (field.maxLength && value.length > field.maxLength) {
            errors[field.name] = `Must be less than ${field.maxLength} characters.`;
            continue;
        }

        if (field.type === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) {
            errors[field.name] = "Please enter a valid email address.";
            continue;
        }

        if (field.type === "tel" && value && !isValidAustralianPhone(value)) {
            errors[field.name] = "Please enter a valid Australian phone number.";
        }
    }

    return errors;
}

function extractFieldErrors(errorBody: any): GoogleContactFormErrors {
    const possibleErrors = errorBody?.fieldErrors ?? errorBody?.errors;

    if (!possibleErrors || typeof possibleErrors !== "object") return {};

    return Object.entries(possibleErrors).reduce<GoogleContactFormErrors>(
        (acc, [key, value]) => {
            if (typeof value === "string") {
                acc[key as GoogleContactFormFieldName] = value;
            }

            return acc;
        },
        {},
    );
}

function buildDefaultPayload({
    values,
    formName,
    captchaToken,
    hiddenPayload,
}: GoogleContactFormBuildPayloadContext): GoogleContactFormPayload {
    const payloadValues = Object.entries(values).reduce<Record<string, string>>(
        (acc, [key, value]) => {
            acc[key] = value.trim();
            return acc;
        },
        {},
    );
    const name = payloadValues.name || [payloadValues.firstName, payloadValues.lastName]
        .map((part) => (part ?? "").trim())
        .filter(Boolean)
        .join(" ");

    return {
        form: formName,
        captchaToken,
        data: {
            ...payloadValues,
            name,
            source: getPageOrigin(),
            pageUrl: getPageUrl(),
            submittedAt: new Date().toISOString(),
            ...hiddenPayload,
        },
    };
}

function FieldStatusIcon({
    isSelect,
    isSuccess,
    error,
}: {
    isSelect?: boolean;
    isSuccess: boolean;
    error?: string;
}) {
    if (error) {
        return (
            <span
                className="google-contact-form-status-icon google-contact-form-status-icon--error"
                aria-hidden="true"
            >
                <AlertTriangle size={20} strokeWidth={2.5} />
            </span>
        );
    }

    if (isSuccess) {
        return (
            <span
                className="google-contact-form-status-icon google-contact-form-status-icon--success"
                aria-hidden="true"
            >
                <CheckCircle2 size={22} strokeWidth={2.2} />
            </span>
        );
    }

    if (isSelect) {
        return (
            <span className="google-contact-form-status-icon" aria-hidden="true">
                <ChevronDown size={20} strokeWidth={2.1} />
            </span>
        );
    }

    return <span aria-hidden="true" />;
}

export function GoogleContactForm({
    apiUrl,
    workerUrl,
    formName = "Grow Leads Contact Form",
    captchaElement,
    getCaptchaToken = getDefaultCaptchaToken,
    resetCaptcha = resetDefaultCaptcha,
    buildPayload = buildDefaultPayload,
    schema,
    extraFields = emptyExtraFields,
    serviceOptions = defaultServiceOptions,
    initialValues,
    requiredFields = defaultRequiredFields,
    hiddenPayload,
    title = "Need a Quick Quote?",
    subtitle = "Get a fast, no-obligation quote from our local Sydney pest control team.",
    servicePlaceholder = "Select a service",
    submitText = "Get My Free Quote",
    loadingText = "Sending Request...",
    successTitle = "Quote Request Sent",
    successMessage = "Thanks - our team will get back to you shortly.",
    errorTitle = "There was a problem sending your request.",
    errorMessage = "Please check the highlighted fields and try again.",
    captchaRequiredMessage = "Please complete the Google reCAPTCHA before sending.",
    footerText = "We'll get back to you within minutes during business hours.",
    loadingFooterText = "We're submitting your request. Please wait a moment...",
    errorFooterText = "Your information is safe and will never be shared.",
    trustItems = defaultTrustItems,
    image,
    accentColor = "#ef1f1f",
    className,
    style,
    statusOverride,
    errorsOverride,
    onSuccess,
    onError,
}: GoogleContactFormProps) {
    const endpoint = apiUrl ?? workerUrl ?? "";
    const resolvedSchema = useMemo<GoogleContactFormSchema>(() => {
        const baseSchema =
            schema ??
            buildDefaultSchema(serviceOptions, servicePlaceholder, requiredFields);

        return {
            fields: [...baseSchema.fields, ...extraFields],
        };
    }, [schema, serviceOptions, servicePlaceholder, requiredFields, extraFields]);
    const [values, setValues] = useState<GoogleContactFormValues>(() =>
        getInitialValues(resolvedSchema, initialValues),
    );
    const [errors, setErrors] = useState<GoogleContactFormErrors>({});
    const [status, setStatus] = useState<GoogleContactFormStatus>("idle");
    const [serverError, setServerError] = useState<ReactNode | null>(null);
    const schemaStateKey = getSchemaStateKey(resolvedSchema);
    const initialValuesStateKey = JSON.stringify(initialValues ?? {});

    useEffect(() => {
        setValues(getInitialValues(resolvedSchema, initialValues));
    }, [schemaStateKey, initialValuesStateKey]);

    const hiddenPayloadStrings = useMemo(
        () =>
            Object.entries(hiddenPayload ?? {}).reduce<Record<string, string>>(
                (acc, [key, value]) => {
                    acc[key] = toPayloadString(value);
                    return acc;
                },
                {},
            ),
        [hiddenPayload],
    );

    const visualStatus = statusOverride ?? status;
    const visualErrors = errorsOverride ?? errors;
    const isBusy = visualStatus === "loading";
    const isSuccess = visualStatus === "success";
    const isError = visualStatus === "error";
    const imagePosition = image?.position ?? "left";
    const imageSrc = image ? getImageSrc(image.src) : undefined;
    const rootStyle: CssVarStyle = {
        "--google-contact-form-accent": accentColor,
        "--google-contact-form-image-src": imageSrc
            ? `url(${JSON.stringify(imageSrc)})`
            : undefined,
        "--google-contact-form-image-position": image?.objectPosition ?? "center",
        "--google-contact-form-image-size": image
            ? getImageBackgroundSize(image.objectFit)
            : undefined,
        "--google-contact-form-image-aspect-ratio": image?.aspectRatio,
        "--google-contact-form-image-background": image?.background,
        ...style,
    };

    function updateField(name: GoogleContactFormFieldName, value: string) {
        setValues((current) => ({
            ...current,
            [name]: value,
        }));
        setErrors((current) => {
            const next = { ...current };
            delete next[name];
            return next;
        });

        if (!statusOverride && status !== "idle") {
            setStatus("idle");
            setServerError(null);
        }
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (isBusy || statusOverride) return;

        const validationErrors = validateValues(values, resolvedSchema.fields);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setStatus("error");
            setServerError(errorMessage);
            return;
        }

        if (!endpoint) {
            const endpointError = new Error("GoogleContactForm requires apiUrl or workerUrl.");
            setStatus("error");
            setServerError("The form endpoint is missing.");
            onError?.(endpointError);
            return;
        }

        setStatus("loading");
        setServerError(null);

        let payload: GoogleContactFormPayload | undefined;

        try {
            const captchaToken = await getCaptchaToken();

            if (!captchaToken) {
                setErrors({ captcha: captchaRequiredMessage });
                setStatus("error");
                setServerError(captchaRequiredMessage);
                return;
            }

            payload = buildPayload({
                values,
                formName,
                captchaToken,
                hiddenPayload: hiddenPayloadStrings,
                schema: resolvedSchema,
            });

            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const body = await response.json().catch(() => null);

            if (!response.ok) {
                const responseErrors = extractFieldErrors(body);
                if (Object.keys(responseErrors).length > 0) {
                    setErrors(responseErrors);
                }

                throw new Error(body?.error ?? body?.message ?? "REQUEST_REJECTED");
            }

            setErrors({});
            setStatus("success");
            resetCaptcha();
            onSuccess?.(body, payload);
        } catch (error) {
            setStatus("error");
            setServerError(errorMessage);
            resetCaptcha();
            onError?.(error, payload);
        }
    }

    function renderSchemaField(field: GoogleContactFormSchemaField) {
        if (field.type === "hidden") {
            return (
                <input
                    key={field.name}
                    type="hidden"
                    name={field.name}
                    value={values[field.name] ?? ""}
                    readOnly
                />
            );
        }

        const fieldError = visualErrors[field.name];
        const Icon = getDefaultFieldIcon(field);
        const value = values[field.name] ?? "";
        const inputId = `google-contact-form-${field.name}`;
        const isDisabled = isBusy || field.disabled;
        const hasStatusIcon = Boolean(fieldError) || isSuccess;

        if (field.type === "select" || field.type === "dropdown") {
            return (
                <div
                    key={field.name}
                    className={clsx(
                        "google-contact-form-field",
                        fieldError && "google-contact-form-field--error",
                    )}
                >
                    <label className="google-contact-form-control" htmlFor={inputId}>
                        <span className="google-contact-form-field-icon" aria-hidden="true">
                            <Icon size={19} strokeWidth={2} />
                        </span>
                        <span className="google-contact-form-field-body">
                            <span className="google-contact-form-label">{field.label}</span>
                            <select
                                id={inputId}
                                className="google-contact-form-select"
                                name={field.name}
                                value={value}
                                disabled={isDisabled}
                                aria-invalid={Boolean(fieldError)}
                                onChange={(event) =>
                                    updateField(field.name, event.target.value)
                                }
                            >
                                <option value="">{field.placeholder ?? "Select an option"}</option>
                                {field.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </span>
                        <FieldStatusIcon
                            isSelect
                            isSuccess={isSuccess}
                            error={fieldError}
                        />
                    </label>
                    {fieldError ? (
                        <p className="google-contact-form-error">{fieldError}</p>
                    ) : null}
                </div>
            );
        }

        if (field.type === "checkbox") {
            return (
                <div
                    key={field.name}
                    className={clsx(
                        "google-contact-form-field",
                        fieldError && "google-contact-form-field--error",
                    )}
                >
                    <label
                        className={clsx(
                            "google-contact-form-control google-contact-form-checkbox-control",
                            !hasStatusIcon && "google-contact-form-control--no-status",
                        )}
                        htmlFor={inputId}
                    >
                        <input
                            id={inputId}
                            className="google-contact-form-checkbox-input"
                            name={field.name}
                            type="checkbox"
                            checked={isCheckboxChecked(field, value)}
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldError)}
                            onChange={(event) =>
                                updateField(
                                    field.name,
                                    event.target.checked
                                        ? getCheckboxCheckedValue(field)
                                        : getCheckboxUncheckedValue(field),
                                )
                            }
                        />
                        <span className="google-contact-form-field-body">
                            <span className="google-contact-form-checkbox-text">
                                {field.checkboxLabel ?? field.label}
                            </span>
                            {field.description ? (
                                <span className="google-contact-form-checkbox-description">
                                    {field.description}
                                </span>
                            ) : null}
                        </span>
                        {hasStatusIcon ? (
                            <FieldStatusIcon isSuccess={isSuccess} error={fieldError} />
                        ) : null}
                    </label>
                    {fieldError ? (
                        <p className="google-contact-form-error">{fieldError}</p>
                    ) : null}
                </div>
            );
        }

        if (field.type === "textarea") {
            return (
                <div
                    key={field.name}
                    className={clsx(
                        "google-contact-form-field google-contact-form-field--textarea",
                        fieldError && "google-contact-form-field--error",
                    )}
                >
                    <label
                        className={clsx(
                            "google-contact-form-control",
                            !hasStatusIcon && "google-contact-form-control--no-status",
                        )}
                        htmlFor={inputId}
                    >
                        <span className="google-contact-form-field-icon" aria-hidden="true">
                            <Icon size={19} strokeWidth={2} />
                        </span>
                        <span className="google-contact-form-field-body">
                            <span className="google-contact-form-label">{field.label}</span>
                            <textarea
                                id={inputId}
                                className="google-contact-form-textarea"
                                name={field.name}
                                value={value}
                                placeholder={field.placeholder}
                                rows={field.rows}
                                disabled={isDisabled}
                                aria-invalid={Boolean(fieldError)}
                                onChange={(event) =>
                                    updateField(field.name, event.target.value)
                                }
                            />
                        </span>
                        {hasStatusIcon ? (
                            <FieldStatusIcon isSuccess={isSuccess} error={fieldError} />
                        ) : null}
                    </label>
                    {fieldError ? (
                        <p className="google-contact-form-error">{fieldError}</p>
                    ) : null}
                </div>
            );
        }

        return (
            <div
                key={field.name}
                className={clsx(
                    "google-contact-form-field",
                    fieldError && "google-contact-form-field--error",
                )}
            >
                <label
                    className={clsx(
                        "google-contact-form-control",
                        !hasStatusIcon && "google-contact-form-control--no-status",
                    )}
                    htmlFor={inputId}
                >
                    <span className="google-contact-form-field-icon" aria-hidden="true">
                        <Icon size={19} strokeWidth={2} />
                    </span>
                    <span className="google-contact-form-field-body">
                        <span className="google-contact-form-label">{field.label}</span>
                        <input
                            id={inputId}
                            className="google-contact-form-input"
                            name={field.name}
                            type={field.type}
                            value={value}
                            placeholder={field.placeholder}
                            autoComplete={field.autoComplete}
                            minLength={field.minLength}
                            maxLength={field.maxLength}
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldError)}
                            onChange={(event) => updateField(field.name, event.target.value)}
                        />
                    </span>
                    {hasStatusIcon ? (
                        <FieldStatusIcon isSuccess={isSuccess} error={fieldError} />
                    ) : null}
                </label>
                {fieldError ? (
                    <p className="google-contact-form-error">{fieldError}</p>
                ) : null}
            </div>
        );
    }

    function renderSchemaFields() {
        const nodes: ReactNode[] = [];
        let halfRow: ReactNode[] = [];

        const flushHalfRow = () => {
            if (halfRow.length === 0) return;

            if (halfRow.length === 1) {
                nodes.push(halfRow[0]);
            } else {
                nodes.push(
                    <div
                        key={`row-${nodes.length}`}
                        className="google-contact-form-row"
                    >
                        {halfRow}
                    </div>,
                );
            }

            halfRow = [];
        };

        for (const field of resolvedSchema.fields) {
            const fieldNode = renderSchemaField(field);

            if (field.type === "hidden") {
                nodes.push(fieldNode);
                continue;
            }

            if (field.width === "half") {
                halfRow.push(fieldNode);

                if (halfRow.length === 2) {
                    flushHalfRow();
                }

                continue;
            }

            flushHalfRow();
            nodes.push(fieldNode);
        }

        flushHalfRow();

        return nodes;
    }

    const footerCopy = isBusy
        ? loadingFooterText
        : isError
          ? errorFooterText
          : footerText;

    return (
        <section
            data-component="google-contact-form"
            className={clsx(
                "google-contact-form",
                image && "google-contact-form--with-image",
                image && `google-contact-form--image-${imagePosition}`,
                className,
            )}
            style={rootStyle}
        >
            <style>{googleContactFormCss}</style>
            {image ? (
                <span className="google-contact-form-background-description">
                    {image.alt}
                </span>
            ) : null}

            <div className="google-contact-form-shell">
                <div className="google-contact-form-card">
                <div className="google-contact-form-trust" aria-label="Trust signals">
                    {trustItems.map((item) => {
                        const Icon = trustIcons[item.icon ?? "star"];

                        return (
                            <div className="google-contact-form-trust-item" key={item.label}>
                                <span
                                    className="google-contact-form-trust-icon"
                                    aria-hidden="true"
                                >
                                    <Icon size={18} strokeWidth={2.2} />
                                </span>
                                <span>{item.label}</span>
                            </div>
                        );
                    })}
                </div>

                <header className="google-contact-form-header">
                    <h2 className="google-contact-form-title">{title}</h2>
                    <div className="google-contact-form-subtitle">{subtitle}</div>
                </header>

                {isSuccess ? (
                    <div className="google-contact-form-state-banner google-contact-form-state-banner--success">
                        <span className="google-contact-form-state-icon" aria-hidden="true">
                            <Check size={22} strokeWidth={2.8} />
                        </span>
                        <div>
                            <p className="google-contact-form-state-title">{successTitle}</p>
                            <p className="google-contact-form-state-message">
                                {successMessage}
                            </p>
                        </div>
                    </div>
                ) : null}

                {isError ? (
                    <div className="google-contact-form-state-banner google-contact-form-state-banner--error">
                        <span className="google-contact-form-state-icon" aria-hidden="true">
                            <AlertTriangle size={22} strokeWidth={2.4} />
                        </span>
                        <div>
                            <p className="google-contact-form-state-title">{errorTitle}</p>
                            <p className="google-contact-form-state-message">
                                {serverError ?? errorMessage}
                            </p>
                        </div>
                    </div>
                ) : null}

                <form className="google-contact-form-form" onSubmit={handleSubmit}>
                    {renderSchemaFields()}

                    {captchaElement ? (
                        <div className="google-contact-form-field">
                            <div className="google-contact-form-captcha">
                                {captchaElement}
                            </div>
                            {visualErrors.captcha ? (
                                <p className="google-contact-form-error">
                                    {visualErrors.captcha}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <button
                        className="google-contact-form-submit"
                        type="submit"
                        disabled={isBusy}
                    >
                        {isBusy ? (
                            <LoaderCircle
                                className="google-contact-form-spinner"
                                size={24}
                                strokeWidth={2.6}
                            />
                        ) : isSuccess ? (
                            <Check size={24} strokeWidth={3} />
                        ) : (
                            <Send size={23} strokeWidth={2.4} />
                        )}
                        <span>
                            {isBusy ? loadingText : isSuccess ? "Request Sent" : submitText}
                        </span>
                    </button>
                </form>

                <div className="google-contact-form-footer">
                    <Lock size={16} strokeWidth={2.3} aria-hidden="true" />
                    <span>{footerCopy}</span>
                </div>
                </div>
            </div>
        </section>
    );
}

export default GoogleContactForm;
